import { useLocation } from 'react-router-dom';
import { useCallback, useContext, useMemo } from 'react';
import reorderArray from 'utils/reorderArray';
import dayjs from 'dayjs';
import { store } from 'components/Store';
import parliamentarianUsers from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import useDashboardUrlCondition from 'components/Dashboard/Navigation/useDashboardUrlCondition';
import useResearchItemsUrl from 'components/Dashboard/Navigation/useResearchItemsUrl';
import getAppSite from 'utils/getAppSite';
import { useAuth0 } from '@auth0/auth0-react';

const useNavSections = ({
  linksContainerRef,
  navPagesRef,
  actionsElementRef,
  onShowAnalyticsPopup,
  onShowReportsPopup,
}) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { activePlan, selectedCrmContacts, selectedGroups } = state ?? {};
  const { code, enableAnalytics, enableReports, teamName } = activePlan ?? {};
  const parliamentarianUser = parliamentarianUsers(code);
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();
  const { pathname: locationPathname, search } = useLocation();
  const { windowWidth } = useWindowHeight();
  const isMobile = useIsMobile();
  const { allowedDashboardUsers } = useDashboardUrlCondition();
  const { stakeholderUrl, policymakerUrl, monitorUrl } = useResearchItemsUrl();
  const { user } = useAuth0();

  const closeMenu = useCallback(() => {
    const hamburgerMenu = document.getElementsByClassName('hamburger')[0];
    hamburgerMenu?.classList.remove('is-active');
    dispatch({ type: 'SHOW_HIDE_MENU', value: true });
  }, [dispatch]);

  const hideInfluenceSection =
    parliamentarianUser || (teamName === 'NCA' && user?.email !== 'james.mitchell@nca.gov.uk');

  const isContactsSelected =
    selectedCrmContacts?.selectedIds?.length > 0 ||
    selectedCrmContacts?.selectedAll ||
    selectedCrmContacts?.selectedAllVisible?.length > 0;

  const isGroupsSelected = selectedGroups?.selectedIds?.length > 0 || selectedGroups?.selectedAll;
  const activeSubNavCondition = isContactsSelected || isGroupsSelected;

  const links = useMemo(
    () => [
      {
        id: 'dashboard_link',
        label: 'Dashboard',
        listClassname: 'text-align-center',
        to: '/dashboard',
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} static-page-monitor topnav-monitor-link static-page-link`,
        'data-intercom-target': 'Dashboard link',
        onClick: closeMenu,
        condition: allowedDashboardUsers,
        pathname: 'dashboard',
      },
      {
        id: 'monitor_link',
        label: 'Monitor',
        listClassname: 'text-align-center',
        to: monitorUrl,
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} static-page-monitor topnav-monitor-link static-page-link ${
            locationPathname.includes('/monitor-inbox') ? 'active-page ' : ''
          }`,
        'data-intercom-target': 'Monitor link',
        onClick: closeMenu,
        condition: true,
        pathname: 'monitor-inbox',
      },
      {
        id: 'campaigns_link',
        label: 'Campaigns',
        listClassname: 'text-align-center',
        to: '/campaigns',
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} topnav-monitor-link static-page-link campaigns-page`,
        'data-intercom-target': 'Campaigns link',
        onClick: closeMenu,
        condition: parliamentarianUser,
        pathname: 'campaigns',
      },
      {
        id: 'stakeholder_mapping_link',
        label: 'People',
        listClassname: 'text-align-center',
        to: '/people/directory',
        className: `static-page-link topnav-influence-link ${
          locationPathname.includes('/people')
            ? `${activeSubNavCondition ? 'active-page-active-subnav' : 'active-page'}`
            : ''
        }`,
        onClick: closeMenu,
        'data-intercom-target': 'Influence link',
        condition: !hideInfluenceSection,
        pathname: 'people/directory',
      },
      {
        id: 'parliamentarians_link',
        label: 'Policymakers by topic',
        listClassname: 'text-align-center',
        to: parliamentarianUser ? policymakerUrl : stakeholderUrl,
        className: ({ isActive }) => `${isActive ? 'active-page' : ''} static-page-link topnav-influence-link`,
        onClick: closeMenu,
        'data-intercom-target': 'Parliamentarian stakeholder mapping link',
        condition: parliamentarianUser,
        pathname: parliamentarianUser ? 'policymakers' : 'people/stakeholder-mapping',
      },
      {
        id: 'analytics_link',
        label: 'Analytics',
        listClassname: `text-align-center ${newAgencyPlan ? '' : 'static-page-link-analytics-container'}`,
        to: `/analytics/dashboard${
          getUrlParam('topic-id') && getUrlParam('topic-id') !== 'all'
            ? `?topic-id=${getUrlParam('topic-id')}`
            : `${getUrlParam('search') && locationPathname.startsWith('/analytics') ? search : ''}`
        }`,
        className: ({ isActive }) =>
          `${isActive ? 'active-page' : ''} static-page-link topnav-influence-link ${
            newAgencyPlan ? '' : 'analytics-static-link'
          } ${locationPathname.match(/\/analytics\/\w+/i)?.input ? 'active-page' : ''}`,
        onClick: (e) => {
          if (!enableAnalytics) {
            e.preventDefault();
            onShowAnalyticsPopup?.();
          }
          closeMenu();
        },
        'data-intercom-target': 'Analytics page',
        condition: true,
        disabled: !enableAnalytics,
        pathname: 'analytics/dashboard',
      },
      {
        id: 'reports_link',
        label: 'Report builder',
        listClassname: 'text-align-center static-page-link-reports-container d-none d-lg-block',
        to: '/reports',
        className: `static-page-link topnav-influence-link reports-static-link ${
          locationPathname.includes('/reports') ? 'active-page' : ''
        } d-none d-lg-block`,
        onClick: (e) => {
          if (!enableReports) {
            e.preventDefault();
            onShowReportsPopup?.();
          }
          closeMenu();
        },
        'data-intercom-target': 'Reports link',
        condition: !agencyUser,
        disabled: !enableReports,
        pathname: 'reports',
      },
      {
        id: 'brief_policymakers_link',
        label: 'Brief policymakers',
        listClassname: 'text-align-center',
        to: '/brief-policymakers',
        className: `static-page-link topnav-influence-link ${
          locationPathname.includes('/brief-policymakers') ? 'active-page' : ''
        }`,
        onClick: closeMenu,
        'data-intercom-target': 'Brief policymakers link',
        condition: !parliamentarianUser && getAppSite() !== 'usa',
        pathname: 'brief-policymakers',
      },
      {
        id: 'ai_assistant_link',
        label: 'AI assistant',
        listClassname: 'text-align-center',
        to: '/ai-assistant',
        className: `static-page-link topnav-influence-link ${
          locationPathname.match(/\/ai-assistant\/content\/\w+/i)?.input ? 'ai-assistant-content' : ''
        } ${locationPathname.includes('/ai-assistant') ? 'active-page' : ''}`,
        onClick: closeMenu,
        'data-intercom-target': 'Writing assistant link',
        condition: parliamentarianUser,
        pathname: 'ai-assistant',
      },
      {
        id: 'manifesto_tracker__link',
        label: 'Devolved elections',
        listClassname: 'text-align-center',
        to: '/devolved-elections-2026/manifestos',
        className: `static-page-link topnav-influence-link ${
          locationPathname.includes('/devolved-elections-2026/manifestos') ? 'active-page' : ''
        }`,
        onClick: closeMenu,
        'data-intercom-target': 'Devolved election manifesto tracker link',
        pathname: 'devolved-elections-2026/manifestos',
        condition: false, //AE: In case we need this feature in the future again
      },
      {
        id: 'calendar_link',
        label: 'Calendar',
        listClassname: 'text-align-center',
        to: `/calendar/${dayjs().format('YYYY-MM-DD')}`,
        className: ({ isActive }) => `${isActive ? 'active-page' : ''} static-page-link topnav-calendar-link`,
        'data-intercom-target': 'Calendar link',
        onClick: closeMenu,
        condition: isMobile && !newAgencyPlan && getAppSite() !== 'usa',
        pathname: 'calendar',
      },
    ],
    [
      closeMenu,
      allowedDashboardUsers,
      monitorUrl,
      locationPathname,
      parliamentarianUser,
      stakeholderUrl,
      policymakerUrl,
      activeSubNavCondition,
      hideInfluenceSection,
      newAgencyPlan,
      enableAnalytics,
      enableReports,
      agencyUser,
      isMobile,
      onShowAnalyticsPopup,
      onShowReportsPopup,
      search,
    ]
  );

  const linksToRender = useMemo(() => {
    let linksToReturn = [...links];
    const notAccessCondition = !enableAnalytics || !enableReports;
    const aiAssistantPosition = linksToReturn.findIndex((link) => link.id === 'ai_assistant_link');
    const devolvedElectionPosition = linksToReturn.findIndex((link) => link.id === 'manifesto_tracker__link');
    if (parliamentarianUser) {
      const monitorPosition = linksToReturn.findIndex((link) => link.id === 'monitor_link');
      linksToReturn = reorderArray(aiAssistantPosition, monitorPosition + 1, linksToReturn);

      const reportsLinkPosition = linksToReturn.findIndex((link) => link.id === 'reports_link');
      const analyticsLinkPosition = linksToReturn.findIndex((link) => link.id === 'analytics_link');
      linksToReturn = reorderArray(analyticsLinkPosition, reportsLinkPosition + 1, linksToReturn);
    }

    if (notAccessCondition) {
      const peoplePosition = linksToReturn.findIndex((link) => link.id === 'stakeholder_mapping_link');
      linksToReturn = reorderArray(aiAssistantPosition, peoplePosition + 1, linksToReturn);

      const briefPolicymakersLink = linksToReturn.findIndex((link) => link.id === 'brief_policymakers_link');
      const reportsLinkPosition = linksToReturn.findIndex((link) => link.id === 'reports_link');
      let analyticsLinkPosition = linksToReturn.findIndex((link) => link.id === 'analytics_link');
      linksToReturn = reorderArray(briefPolicymakersLink, analyticsLinkPosition - 1, linksToReturn);
      if (!enableAnalytics) {
        analyticsLinkPosition = linksToReturn.findIndex((link) => link.id === 'analytics_link');
        linksToReturn = reorderArray(analyticsLinkPosition, reportsLinkPosition + 1, linksToReturn);
      }
      if (devolvedElectionPosition !== -1) {
        const currentDevolvedPosition = linksToReturn.findIndex((link) => link.id === 'manifesto_tracker__link');
        const currentReportsPosition = linksToReturn.findIndex((link) => link.id === 'reports_link');
        linksToReturn = reorderArray(currentDevolvedPosition, currentReportsPosition, linksToReturn);
      }
    }
    if (newAgencyPlan) {
      const reportsPosition = linksToReturn.findIndex((link) => link.id === 'reports_link');
      linksToReturn = reorderArray(
        reportsPosition,
        devolvedElectionPosition >= 0 ? devolvedElectionPosition - 1 : aiAssistantPosition + 1,
        linksToReturn
      );
    }
    linksToReturn = [...linksToReturn.filter((link) => link.condition || typeof link.condition === 'undefined')];
    return linksToReturn;
  }, [links, enableAnalytics, enableReports, parliamentarianUser, newAgencyPlan]);

  const { moreLinks, urls } = useMemo(() => {
    const itemsResult = [];
    const urlsResult = [];
    const linksContainer = linksContainerRef?.current;
    const currentlyHiddenItems = linksContainer
      ? [...linksContainer.querySelectorAll('li')].filter((item) => item?.offsetTop > 0)
      : [];

    linksToRender?.forEach((link) => {
      const { label, onClick, disabled, ...linkProps } = link;
      const linkElement = linksContainer?.querySelector(`#${linkProps.id}`);
      const offsetAdjustment = Math.ceil((linkElement?.parentElement?.offsetHeight - linkElement?.offsetHeight) / 2);

      if (linkElement?.offsetTop > offsetAdjustment) {
        const lastItemFitsCondition =
          currentlyHiddenItems?.length === 1 &&
          !!currentlyHiddenItems?.[0]?.querySelector(`#${linkProps.id}`) &&
          actionsElementRef?.current &&
          linkElement?.offsetWidth <= 116;

        const validateActiveClass =
          (linkProps.to.startsWith('/analytics/dashboard') && /^\/analytics\/[\w-]+/.test(locationPathname)) ||
          linkProps.to?.startsWith(locationPathname) ||
          locationPathname.startsWith(linkProps.to);

        if (lastItemFitsCondition && navPagesRef.current) {
          navPagesRef.current.style.paddingRight = 0;
        } else {
          itemsResult.push({
            linkProps,
            label,
            disabled,
            onClick,
            key: `item-${link?.id}`,
            className: `w-100 d-block text-left general-button list-action-button px-3 py-2 reset-link reset-link-color text-main-blue ${
              validateActiveClass ? 'active' : ''
            }`,
          });
          urlsResult.push(linkProps.to);
        }
      }
    });

    return { moreLinks: itemsResult, urls: urlsResult };
  }, [linksToRender, linksContainerRef, navPagesRef, actionsElementRef, windowWidth, locationPathname]);

  return { linksToRender, moreLinks, urls };
};

export default useNavSections;
